import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetCampaignQuery, useUpdateCampaignMutation, useGetCampaignContactsQuery } from "@/store/rest_api";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import CampaignForm from "@/components/pages/campaigns/CampaignForm";
import CampaignContactsSection from "@/components/pages/campaigns/CampaignContactsSection";
import SequenceStatsHeader from "@/components/pages/campaigns/SequenceStatsHeader";
import { CampaignFormData } from "@/model/interface/Campaign";
import { apiErrorMessage } from "@/util/apiError";

export default function EditCampaignPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: campaign, isLoading, error } = useGetCampaignQuery(id!, { skip: !id });
  const { data: contacts, isLoading: contactsLoading } = useGetCampaignContactsQuery(id!, { skip: !id });
  const [updateCampaign, { isLoading: isUpdating }] = useUpdateCampaignMutation();

  const handleSubmit = async (data: CampaignFormData) => {
    if (!id) {
      return;
    }
    try {
      await updateCampaign({ id, data }).unwrap();
      toast.success("Campaign updated");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update campaign"));
    }
  };

  if (!id) {
    navigate("/campaigns");
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-sm text-destructive">{apiErrorMessage(error, "Failed to load campaign")}</p>
        <Button variant="ghost" onClick={() => navigate("/campaigns")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Campaigns
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 max-w-3xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Edit Campaign</h1>
        <p className="text-muted-foreground">{campaign.name}</p>
      </div>

      {campaign.type === "SEQUENCE" && <SequenceStatsHeader campaignId={id} />}

      <CampaignForm
        initialData={campaign}
        onSubmit={handleSubmit}
        isSubmitting={isUpdating}
        submitLabel="Save Changes"
        leadsContent={
          <CampaignContactsSection
            campaignId={id}
            contacts={contacts ?? []}
            isLoading={contactsLoading}
            campaignType={campaign.type}
          />
        }
      />
    </div>
  );
}
